import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { isNil } from 'lodash'
import { QuestionnaireForm } from '../forms'
import * as apiActions from 'api-actions'
import { selectors } from '../reducer'
import { Questionnaire, trip, workOrder, QUESTIONNAIRE_TYPE } from 'types'
import {
  useSubmitMessages,
  loadQuestionFormValues,
  prepareQuestionResponses,
} from 'utils'

const propTypes = {
  advanceQuestionnaire: PropTypes.func.isRequired,
  currentQuestionnaire: Questionnaire.isRequired,
  currentTrip: trip,
  workOrder: workOrder,
}
const defaultProps = {
  currentQuestionnaire: {
    type: QUESTIONNAIRE_TYPE.BLANK,
    questions: [],
  },
}

function WorkOrderQuestionnaire({
  advanceQuestionnaire,
  currentQuestionnaire,
  currentTrip,
  workOrder,
}) {
  const history = useHistory()
  const { workOrderId } = useParams()
  const { displaySubmitFailure, clearToasts } = useSubmitMessages()

  // This page is an internal step of the check-in flow, driven entirely by what
  // check-in and each advance response leave in the store. It never fetches:
  // WorkOrderShow owns "is a questionnaire needed?", and the advance response
  // below owns "are we finished?". Bounce to Show if there is no trip (deep link /
  // hard refresh) or the stored trip/work order is for a different id than the URL.
  // Show's leave cleanup may unset workOrder while leaving currentTrip, so a
  // missing work order is not treated as a mismatch.
  const tripBelongsToRoute =
    currentTrip &&
    (isNil(currentTrip.workOrderId) ||
      String(currentTrip.workOrderId) === String(workOrderId))
  const storedWorkOrderMatchesRoute =
    isNil(workOrder) || String(workOrder.id) === String(workOrderId)
  if (!tripBelongsToRoute || !storedWorkOrderMatchesRoute)
    return <Redirect to={`/work_orders/show/${workOrderId}`} />

  const questions = currentQuestionnaire.questions
  const { step } = currentQuestionnaire

  return (
    <section>
      <header>
        {
          <h2>
            Please provide the following information before getting started:
          </h2>
        }
        <h3>Step {step}</h3>
      </header>

      <article>
        <QuestionnaireForm
          initialValues={loadQuestionFormValues(currentTrip, questions)}
          questions={questions}
          key={currentQuestionnaire.type}
          onSubmit={async (questionnaireResponses) => {
            try {
              await clearToasts()
              const filteredFormattedResponses = prepareQuestionResponses(
                questionnaireResponses,
                questions,
                currentTrip
              )
              const updatedWorkOrder = await advanceQuestionnaire(
                currentTrip.id,
                filteredFormattedResponses,
                currentQuestionnaire.type
              )

              // The advance response is the single source of truth for whether the
              // wizard has more to ask. Once nothing is pending we return to the work
              // order page; otherwise the store update re-renders the next step here.
              if (!updatedWorkOrder?.requiresQuestionnaireCompletion) {
                history.replace(`/work_orders/show/${workOrderId}`)
              }
            } catch (error) {
              const errors = error.errors
              displaySubmitFailure(errors, null, error)
            }
          }}
        />
      </article>
    </section>
  )
}

WorkOrderQuestionnaire.propTypes = propTypes
WorkOrderQuestionnaire.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentQuestionnaire: selectors.currentQuestionnaire(state),
    currentTrip: selectors.currentTrip(state),
    workOrder: selectors.workOrder(state),
  }
}

const mapDispatchToProps = {
  advanceQuestionnaire: apiActions.advanceQuestionnaire,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  WorkOrderQuestionnaire
)
